import { memo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { monthNames } from './date-utils';

const cellBase =
  'flex h-11 items-center justify-center rounded-lg text-xs transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';

interface MonthPanelProps {
  year: number;
  selectedYear: number | null;
  selectedMonth: number | null; // 1–12
  locale: string;
  onYearChange: (year: number) => void;
  onSelect: (month: number) => void;
  isMonthDisabled?: (year: number, month: number) => boolean;
}

/** 12-month grid with year stepper — drives `month` / `month-year` modes. */
export const MonthPanel = memo(function MonthPanel({
  year,
  selectedYear,
  selectedMonth,
  locale,
  onYearChange,
  onSelect,
  isMonthDisabled,
}: MonthPanelProps) {
  const names = monthNames(locale, 'short');
  return (
    <div className="w-64">
      <PanelHeader label={String(year)} onPrev={() => onYearChange(year - 1)} onNext={() => onYearChange(year + 1)} />
      <div className="grid grid-cols-3 gap-1.5">
        {names.map((name, i) => {
          const month = i + 1;
          const selected = selectedYear === year && selectedMonth === month;
          const disabled = isMonthDisabled?.(year, month) ?? false;
          return (
            <button
              key={month}
              type="button"
              disabled={disabled}
              aria-selected={selected || undefined}
              onClick={() => onSelect(month)}
              className={cn(
                cellBase,
                selected ? 'bg-primary font-semibold text-primary-foreground' : 'hover:bg-muted',
                disabled && 'cursor-not-allowed text-muted-foreground/40',
              )}
            >
              {name}
            </button>
          );
        })}
      </div>
    </div>
  );
});

interface QuarterPanelProps {
  year: number;
  selectedYear: number | null;
  selectedQuarter: number | null; // 1–4
  onYearChange: (year: number) => void;
  onSelect: (quarter: number) => void;
}

/** Q1–Q4 grid with year stepper — drives `quarter` mode. */
export const QuarterPanel = memo(function QuarterPanel({
  year,
  selectedYear,
  selectedQuarter,
  onYearChange,
  onSelect,
}: QuarterPanelProps) {
  return (
    <div className="w-64">
      <PanelHeader label={String(year)} onPrev={() => onYearChange(year - 1)} onNext={() => onYearChange(year + 1)} />
      <div className="grid grid-cols-2 gap-1.5">
        {[1, 2, 3, 4].map((q) => {
          const selected = selectedYear === year && selectedQuarter === q;
          return (
            <button
              key={q}
              type="button"
              aria-selected={selected || undefined}
              onClick={() => onSelect(q)}
              className={cn(
                cellBase,
                'h-16 flex-col gap-0.5',
                selected ? 'bg-primary font-semibold text-primary-foreground' : 'hover:bg-muted',
              )}
            >
              <span className="text-base font-semibold">Q{q}</span>
              <span className={cn('text-[10px]', selected ? 'text-primary-foreground/80' : 'text-muted-foreground')}>
                {monthNames('en-US', 'short')[(q - 1) * 3]}–{monthNames('en-US', 'short')[(q - 1) * 3 + 2]}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
});

interface YearPanelProps {
  /** First year of the visible 12-year page. */
  pageStart: number;
  selectedYear: number | null;
  onPageChange: (pageStart: number) => void;
  onSelect: (year: number) => void;
  minYear: number;
  maxYear: number;
}

/** Paged 12-year grid — drives `year` mode. */
export const YearPanel = memo(function YearPanel({
  pageStart,
  selectedYear,
  onPageChange,
  onSelect,
  minYear,
  maxYear,
}: YearPanelProps) {
  const years = Array.from({ length: 12 }, (_, i) => pageStart + i);
  return (
    <div className="w-64">
      <PanelHeader
        label={`${pageStart}–${pageStart + 11}`}
        onPrev={() => onPageChange(pageStart - 12)}
        onNext={() => onPageChange(pageStart + 12)}
        prevDisabled={pageStart <= minYear}
        nextDisabled={pageStart + 12 > maxYear}
      />
      <div className="grid grid-cols-3 gap-1.5">
        {years.map((y) => {
          const selected = selectedYear === y;
          const disabled = y < minYear || y > maxYear;
          return (
            <button
              key={y}
              type="button"
              disabled={disabled}
              aria-selected={selected || undefined}
              onClick={() => onSelect(y)}
              className={cn(
                cellBase,
                selected ? 'bg-primary font-semibold text-primary-foreground' : 'hover:bg-muted',
                disabled && 'cursor-not-allowed text-muted-foreground/40',
              )}
            >
              {y}
            </button>
          );
        })}
      </div>
    </div>
  );
});

function PanelHeader({
  label,
  onPrev,
  onNext,
  prevDisabled,
  nextDisabled,
}: {
  label: string;
  onPrev: () => void;
  onNext: () => void;
  prevDisabled?: boolean;
  nextDisabled?: boolean;
}) {
  return (
    <div className="mb-3 flex items-center justify-between">
      <NavButton onClick={onPrev} disabled={prevDisabled} label="Previous">
        <ChevronLeft className="h-4 w-4" />
      </NavButton>
      <span className="text-xs font-semibold text-foreground">{label}</span>
      <NavButton onClick={onNext} disabled={nextDisabled} label="Next">
        <ChevronRight className="h-4 w-4" />
      </NavButton>
    </div>
  );
}

function NavButton({
  onClick,
  disabled,
  label,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
    >
      {children}
    </button>
  );
}
